'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, Phone, Eye, EyeOff, ArrowRight, Shield, Loader2 } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type LoginMode = 'email' | 'phone';

export default function LoginPage() {
  const router = useRouter();
  const { signIn, signInWithPhone, verifyOtp } = useAuth();

  const [mode, setMode] = useState<LoginMode>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter email and password'); return; }
    setLoading(true);
    try {
      await signIn(email, password);
      toast.success('Welcome back!');
      router.push('/dashboard');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      toast.error(msg.includes('Invalid') ? 'Invalid email or password' : msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone || phone.length < 10) { toast.error('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      await signInWithPhone(`+91${phone}`);
      setOtpSent(true);
      toast.success('OTP sent to your mobile number');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send OTP';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp || otp.length < 6) { toast.error('Enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      await verifyOtp(`+91${phone}`, otp);
      toast.success('Login successful!');
      router.push('/dashboard');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'OTP verification failed';
      toast.error(msg.includes('expired') ? 'OTP expired. Please request a new one.' : 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-[#1a6bb5] to-[#0a3d6b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center gap-3 mb-4">
            <AppLogo size={48} />
            <div className="text-left">
              <p className="text-white font-extrabold text-2xl leading-none">Ghumo Ji</p>
              <p className="text-blue-200 text-sm">Travels</p>
            </div>
          </Link>
          <h1 className="text-white text-2xl font-bold">Customer Login</h1>
          <p className="text-blue-200 text-sm mt-1">Sign in to manage your bookings</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Mode Toggle */}
          <div className="flex gap-2 mb-6 bg-muted rounded-xl p-1">
            <button
              onClick={() => { setMode('email'); setOtpSent(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${mode === 'email' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Mail size={15} /> Email & Password
            </button>
            <button
              onClick={() => { setMode('phone'); setOtpSent(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${mode === 'phone' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Phone size={15} /> Mobile OTP
            </button>
          </div>

          {mode === 'email' ? (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-10"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-10 pr-10"
                    placeholder="Enter your password"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={otpSent ? handleVerifyOtp : handleSendOtp} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Mobile Number</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-semibold">+91</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="input-field pl-12"
                    placeholder="10-digit mobile number"
                    disabled={otpSent}
                    required
                  />
                </div>
              </div>
              {otpSent && (
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Enter OTP</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input-field text-center text-xl tracking-widest font-bold"
                    placeholder="• • • • • •"
                    maxLength={6}
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">OTP sent to +91 {phone}</p>
                </div>
              )}
              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />}
                {loading ? 'Please wait...' : otpSent ? 'Verify OTP' : 'Send OTP'}
              </button>
              {otpSent && (
                <button type="button" onClick={() => setOtpSent(false)} className="w-full text-sm text-muted-foreground hover:text-primary text-center">
                  Change number or resend OTP
                </button>
              )}
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-border text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              New customer?{' '}
              <Link href="/register" className="text-primary font-semibold hover:underline">Create Account</Link>
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Shield size={12} />
              <span>Secure login powered by Supabase</span>
            </div>
          </div>
        </div>

        <p className="text-center text-blue-200 text-xs mt-6">
          <Link href="/" className="hover:text-white transition-colors">← Back to Homepage</Link>
        </p>
      </div>
    </div>
  );
}
